/// <amd-module name="Wit/Search/InstantSearchProviders/RecentWorkItemsProvider" />
import * as React from "react";
import { announce } from "VSS/Core/Util/Accessibility";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext, Services } from "VSS/Platform/Context";
import { IMenuItem, IMenuItemDetails, MenuCell, MenuItemType } from "VSSUI/Menu";
import { SecuredGenericData } from "Wit/Common/Generated/Contracts";
import { BaseInstantSearchProvider } from "InstantSearch/Shared/InstantSearchProviders/BaseInstantSearchProvider";
import { ResultRow } from "InstantSearch/Shared/ResultRow";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { IInstantSearchMenuService, IMenuServiceContext } from "InstantSearch/Shared/Types";
import { IDisplayWorkItemService } from "Wit/Search/InstantSearchProviders/Services/DisplayWorkItemService";
import { DayZeroMessageProvider } from "Wit/Search/InstantSearchProviders/DayZeroMessageProvider";
import { IPrefetchInstantSearchContentService } from "Wit/Search/PrefetchContent/PrefetchInstantSearchContentService";
import { CustomerIntelligenceConstants } from "Wit/Search/Constants";
import * as Resources from "Wit/Search/Resources";

const maxResultRows = 5;

/**
 * Service for creating menu items for recent work items.
 */
export class RecentWorkItemsProvider extends BaseInstantSearchProvider<SecuredGenericData[] | undefined> implements IInstantSearchMenuService<IMenuServiceContext> {
    protected _contributionId = "";
    private _dayZeroMessageService: DayZeroMessageProvider;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initContribution(this._contributionId);
        this._dayZeroMessageService = this.pageContext.getService<DayZeroMessageProvider>("dayzero-message-provider-service");
    }

    protected _getMenuItems(menuId: string,
        context: IMenuServiceContext,
        invokeProvider: boolean,
        searchText: string
    ): IReadonlyObservableArray<IMenuItem> | null {
        if (invokeProvider && !searchText) {
            this.getSuggestions(
                "",
                context,
                "CachedRecentWorkItemsFetched",
                CustomerIntelligenceConstants.TTISearchScenarioName,
                CustomerIntelligenceConstants.SubsequentSearchScenarioName
            );
        } else {
            this._menuItems.splice(0, this._menuItems.length);
        }

        return this._menuItems;
    }

    protected _fetchResults(searchText: string, context: IMenuServiceContext): Promise<SecuredGenericData[] | undefined> {
        const prefetchInstantSearchContentService = this.pageContext
            .getService<IPrefetchInstantSearchContentService>("IPrefetchInstantSearchContentService");
        return prefetchInstantSearchContentService.recentWorkItemsCache;
    }

    protected _onFetchSuccess(searchText: string, context: IMenuServiceContext, data: SecuredGenericData[] | undefined): void {
        const { searchInput, telemetryWriter, onFocus, invokedProvidersProps } = context;
        this._menuItems.splice(0, this._menuItems.length);

        if (this.shouldInvokeProvider(context) && searchInput.queryText().trim()) {
            // Search text is non-empty. It got changed before fetching results. So, not updating menuItems.
            return;
        }

        if (data && data.length > 0) {
            const header = invokedProvidersProps && invokedProvidersProps.props
                ? invokedProvidersProps.props.emptyTextHeader
                : Resources.RecentWorkItemsHeader;
            this._dayZeroMessageService.updateWorkItemState(true);
            // Add header
            this._menuItems.push({
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            });

            const limitOfWorkItems = Math.min(maxResultRows, data.length);
            for (let index = 0; index < limitOfWorkItems; index++) {
                const wit = data[index].data;
                const project = wit[0];
                const id = wit[1];
                const title = wit[2];
                const type = wit[3];

                this._menuItems.push({
                    id: "",
                    groupKey: this._groupKey,
                    rank: this._rank,
                    renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
                        details.onFocusItem = onFocus;
                        switch (menuCell) {
                            case MenuCell.PrimaryText:
                                return <ResultRow
                                    workItemIconProps={{ project: project, workItemType: type }}
                                    title={title}
                                    mutedText={'#' + id}
                                    mutedTextAriaLabel={format(Resources.WorkItemIdAriaLabel, id)}
                                />;
                            default:
                                return undefined;
                        }
                    },
                    onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) =>
                        this._displayWorkItem(
                            id,
                            project,
                            event && event.ctrlKey,
                            searchInput.setHelpVisibility,
                            () => this._recordWorkItemClickTelemetry(menuItem.data, telemetryWriter))
                });
            }

            announce(Resources.RecentWorkItemsLoadedMessage);
        } else if ((data && data.length === 0) || !data) {
            this._dayZeroMessageService.updateWorkItemState(false);
            telemetryWriter.publish("NoSuggestionsFound", { "type": "RecentWorkItems" });
        }
    }

    protected _onFetchFail(context: IMenuServiceContext, error: Error): void {
        const { invokedProvidersProps } = context;
        const header = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.emptyTextHeader
            : Resources.RecentWorkItemsHeader;
        this._menuItems.splice(0, this._menuItems.length);
        this._menuItems.push(
            {
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            },
            {
                id: "",
                itemType: MenuItemType.Header,
                className: "suggestion-message",
                groupKey: this._groupKey,
                rank: this._rank,
                text: Resources.GenericErrorMessage
            }
        );

        announce(Resources.GenericErrorMessage);
    }

    private _recordWorkItemClickTelemetry = (index: number, telemetryWriter: TelemetryWriter): void => {
        telemetryWriter.publish("ResultClicked", {
            "index": index,
            "resultType": "RecentWorkItem"
        });
    }

    private _displayWorkItem = (
        workItemId: string,
        projectName: string,
        openInNewTab?: boolean,
        setHelpVisibility?: (hideHelp: boolean, focusInputOnShow?: boolean) => void,
        callback?: () => void
    ): void => {
        const displayWorkItemService = this.pageContext.getService<IDisplayWorkItemService>("IDisplayWorkItemService");
        displayWorkItemService.displayWorkItem(workItemId, projectName, openInNewTab, setHelpVisibility, callback);
    }
}

function getRecentWorkItemsProvider(contributionId: string): typeof RecentWorkItemsProvider {
    return class extends RecentWorkItemsProvider {
        protected _contributionId = contributionId;
    }
}

Services.add(
    "project-recent-workitems-provider-service",
    { serviceFactory: getRecentWorkItemsProvider("ms.vss-work-web.recent-workitems-project-menu-provider") }
);

Services.add(
    "collection-recent-workitems-provider-service",
    { serviceFactory: getRecentWorkItemsProvider("ms.vss-work-web.recent-workitems-collection-menu-provider") }
);
